// GameLoop (IMPLEMENTATION §2.1, §2.9): a fixed-step accumulator on its OWN clock — the caller passes
// performance.now() every frame (the FactoryScene's `update`, or the NullRenderer's rAF); Phaser's dt is
// ignored. A frame gap is clamped (render.frameClampMs: a hidden tab or a GC pause never becomes a burst of
// catch-up ticks), at most render.maxTicksPerFrame ticks run per frame (the rest of a backlog is dropped),
// and `speed` scales game time (0 = stopped: ?test boots stopped, tests move the sim with `advance`).
// `alpha` = the fraction of the next tick already elapsed, for RenderSync interpolation.

export interface LoopConfig {
  tickHz: number;
  frameClampMs: number;
  maxTicksPerFrame: number;
}

export interface LoopHost {
  /** One sim tick, with fan-out (or without it when `silent`). */
  step(silent: boolean): void;
}

export class GameLoop {
  private readonly tickMs: number;
  private readonly cfg: LoopConfig;
  private readonly host: LoopHost;
  private acc = 0;
  private last: number | null = null;
  private k: number;

  constructor(cfg: LoopConfig, host: LoopHost, speed = 1) {
    if (!(cfg.tickHz > 0)) throw new RangeError('tickHz must be > 0');
    this.cfg = cfg;
    this.host = host;
    this.tickMs = 1000 / cfg.tickHz;
    this.k = GameLoop.checkSpeed(speed);
  }

  private static checkSpeed(k: number): number {
    if (!Number.isFinite(k) || k < 0)
      throw new RangeError(`speed must be a finite number ≥ 0 (got ${k})`);
    return k;
  }

  /** Runs the ticks due at `nowMs` and returns alpha ∈ [0, 1). */
  frame(nowMs: number): number {
    if (this.last === null) {
      this.last = nowMs;
      return this.alpha();
    }
    let dt = nowMs - this.last;
    this.last = nowMs;
    if (!(dt > 0)) dt = 0;
    if (dt > this.cfg.frameClampMs) dt = this.cfg.frameClampMs;
    this.acc += dt * this.k;
    let n = 0;
    while (this.acc >= this.tickMs && n < this.cfg.maxTicksPerFrame) {
      this.acc -= this.tickMs;
      n += 1;
      this.host.step(false);
    }
    // a backlog beyond the per-frame cap is dropped, not carried (no spiral of death)
    if (this.acc >= this.tickMs) this.acc %= this.tickMs;
    return this.alpha();
  }

  /** Fraction of the next tick elapsed (0 while stopped). */
  alpha(): number {
    if (this.k === 0) return 0;
    const a = this.acc / this.tickMs;
    return a < 0 ? 0 : a >= 1 ? 0.999999 : a;
  }

  /** Runs `n` ticks now (tests, debug); `silent` suppresses the cosmetic fan-out (the host rebuilds views). */
  advance(n: number, silent = false): void {
    if (!Number.isSafeInteger(n) || n < 0)
      throw new RangeError(`advance: n must be an integer ≥ 0 (got ${n})`);
    for (let i = 0; i < n; i++) this.host.step(silent);
  }

  setSpeed(k: number): void {
    this.k = GameLoop.checkSpeed(k);
    if (this.k === 0) this.acc = 0;
  }

  get speed(): number {
    return this.k;
  }

  /** Forget the last frame time (the next frame starts a fresh interval, e.g. after a resume). */
  resetClock(): void {
    this.last = null;
    this.acc = 0;
  }
}
